import React, { useState, useEffect } from 'react';
import { Box, Typography, Button, IconButton, LinearProgress, Chip } from '@mui/material';
import { useNavigate } from 'react-router-dom';
import CloseIcon from '@mui/icons-material/Close';
import ArrowForwardIcon from '@mui/icons-material/ArrowForward';
import ArrowBackIcon from '@mui/icons-material/ArrowBack';
import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import OpenInNewIcon from '@mui/icons-material/OpenInNew';
import StorageIcon from '@mui/icons-material/Storage';
import FolderIcon from '@mui/icons-material/Folder';
import SmartToyIcon from '@mui/icons-material/SmartToy';
import SecurityIcon from '@mui/icons-material/Security';
import RocketLaunchIcon from '@mui/icons-material/RocketLaunch';
import { TabIndex } from '../navigation';

// Import real platform icons
import cloudSvg from '../assets/icons-home/cloud.svg';
import flashgateLogo from '../assets/logowhite.png';
import awsLogo from '../assets/cloud-providers/aws.svg';
import googleCloudLogo from '../assets/cloud-providers/google-cloud-icon.svg';
import azureLogo from '../assets/cloud-providers/azure.svg';

// Platform Icons (matching Sidebar.tsx)
const CloudStorageIcon = ({ size = 20, color = '#10B981' }: { size?: number; color?: string }) => (
  <img src={cloudSvg} alt="Cloud" style={{ width: size, height: size }} />
);

const DepinHubIcon = ({ size = 20, color = '#06B6D4' }: { size?: number; color?: string }) => (
  <svg xmlns="http://www.w3.org/2000/svg" width={size} height={size} viewBox="0 0 24 24" fill="none" stroke={color} strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
    <circle cx="12" cy="12" r="3" />
    <circle cx="5" cy="5" r="2" />
    <circle cx="19" cy="5" r="2" />
    <circle cx="5" cy="19" r="2" />
    <circle cx="19" cy="19" r="2" />
    <line x1="12" y1="9" x2="5" y2="7" />
    <line x1="12" y1="9" x2="19" y2="7" />
    <line x1="12" y1="15" x2="5" y2="17" />
    <line x1="12" y1="15" x2="19" y2="17" />
  </svg>
);

const AIIcon = ({ size = 20, color = '#8B5CF6' }: { size?: number; color?: string }) => (
  <svg xmlns="http://www.w3.org/2000/svg" width={size} height={size} viewBox="0 0 24 24" fill="none" stroke={color} strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
    <path d="M12 2L2 7l10 5 10-5-10-5z" />
    <path d="M2 17l10 5 10-5" />
    <path d="M2 12l10 5 10-5" />
  </svg>
);

const NetworkIcon = ({ size = 20, color = '#F97316' }: { size?: number; color?: string }) => (
  <svg xmlns="http://www.w3.org/2000/svg" width={size} height={size} viewBox="0 0 24 24" fill="none" stroke={color} strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
    <path d="M21 16V8a2 2 0 0 0-1-1.73l-7-4a2 2 0 0 0-2 0l-7 4A2 2 0 0 0 3 8v8a2 2 0 0 0 1 1.73l7 4a2 2 0 0 0 2 0l7-4A2 2 0 0 0 21 16z" />
    <polyline points="7.5 4.21 12 6.81 16.5 4.21" />
    <polyline points="7.5 19.79 7.5 14.6 3 12" />
    <polyline points="21 12 16.5 14.6 16.5 19.79" />
    <polyline points="3.27 6.96 12 12.01 20.73 6.96" />
    <line x1="12" x2="12" y1="22.08" y2="12" />
  </svg>
);

const RepositoriesIcon = ({ size = 20, color = '#617BFF' }: { size?: number; color?: string }) => (
  <svg xmlns="http://www.w3.org/2000/svg" width={size} height={size} viewBox="0 0 24 24" fill="none" stroke={color} strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
    <path d="M4 19.5A2.5 2.5 0 0 1 6.5 17H20" />
    <path d="M6.5 2H20v20H6.5A2.5 2.5 0 0 1 4 19.5v-15A2.5 2.5 0 0 1 6.5 2z" />
  </svg>
);

const ChatIcon = ({ size = 20, color = '#EC4899' }: { size?: number; color?: string }) => (
  <svg xmlns="http://www.w3.org/2000/svg" width={size} height={size} viewBox="0 0 24 24" fill="none" stroke={color} strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
    <path d="M21 15a2 2 0 0 1-2 2H7l-4 4V5a2 2 0 0 1 2-2h14a2 2 0 0 1 2 2z" />
  </svg>
);

const OrganizationIcon = ({ size = 20, color = '#6366F1' }: { size?: number; color?: string }) => (
  <svg xmlns="http://www.w3.org/2000/svg" width={size} height={size} viewBox="0 0 24 24" fill="none" stroke={color} strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
    <rect x="16" y="16" width="6" height="6" rx="1" />
    <rect x="2" y="16" width="6" height="6" rx="1" />
    <rect x="9" y="2" width="6" height="6" rx="1" />
    <path d="M5 16v-3a1 1 0 0 1 1-1h12a1 1 0 0 1 1 1v3" />
    <path d="M12 12V8" />
  </svg>
);

interface OnboardingTutorialProps {
  open: boolean;
  onClose: () => void;
  onComplete: () => void;
  userName?: string;
}

interface TutorialFeature {
  icon: React.ReactNode;
  title: string;
  description: string;
  path?: string;
  trackingKey?: string;
}

interface TutorialStep {
  id: string;
  title: string;
  subtitle?: string;
  description: string;
  features?: TutorialFeature[];
  illustration: React.ReactNode;
  color: string;
  bgGradient: string;
  mainPath?: string;
}

const OnboardingTutorial: React.FC<OnboardingTutorialProps> = ({
  open,
  onClose,
  onComplete,
  userName = 'User',
}) => {
  const navigate = useNavigate();
  const [currentStep, setCurrentStep] = useState(0);
  const [isAnimating, setIsAnimating] = useState(false);
  const [visitedSections, setVisitedSections] = useState<string[]>(() => {
    try {
      const saved = localStorage.getItem('tutorialVisitedSections');
      return saved ? JSON.parse(saved) : [];
    } catch {
      return [];
    }
  });

  // Save visited sections to localStorage
  useEffect(() => {
    try {
      localStorage.setItem('tutorialVisitedSections', JSON.stringify(visitedSections));
    } catch { void 0; }
  }, [visitedSections]);

  // Mark section as visited
  const markAsVisited = (key: string) => {
    if (!visitedSections.includes(key)) {
      setVisitedSections(prev => [...prev, key]);
    }
  };

  // Map paths to TabIndex for proper navigation
  const pathToTabIndex: Record<string, number> = {
    '/cloud-storage/buckets': TabIndex.StorageUnits,
    '/cloud-storage/overview': TabIndex.Dashboard,
    '/metrics': TabIndex.Metrics,
    '/ai/llm': TabIndex.AILLM,
    '/ai/policies': TabIndex.AIPolicies,
    '/ai/statistics': TabIndex.AIStatistics,
    '/ai/overview': TabIndex.AIOverview,
    '/depin/storage-units': TabIndex.StorageUnitsDePIN,
    '/depin/marketplace': TabIndex.Marketplace,
    '/depin/deals': TabIndex.MyDeals,
    '/network/public': TabIndex.BridgeNodesPublic,
    '/network/private': TabIndex.BridgeNodesPrivate,
    '/repositories/inventory': TabIndex.StorageRepos,
    '/repositories/api-keys': TabIndex.APIKeys,
    '/repositories/overview': TabIndex.RepoOverview,
    '/chat': TabIndex.Chat,
    '/organization': TabIndex.Organization,
    '/workspace/home': TabIndex.Home,
  };

  // Navigate to a section using TabIndex and custom event
  const handleNavigateToSection = (path: string, trackingKey?: string) => {
    if (trackingKey) {
      markAsVisited(trackingKey);
    }
    
    // Save current step to localStorage so user can return to tutorial
    try {
      localStorage.setItem('tutorialInProgress', 'true');
      localStorage.setItem('tutorialCurrentStep', String(currentStep));
    } catch { void 0; }
    
    onClose();
    
    // Get the corresponding TabIndex
    const tabIndex = pathToTabIndex[path];
    
    if (tabIndex !== undefined) {
      // Dispatch custom event to App.tsx for tab navigation
      const rootElement = document.getElementById('root');
      if (rootElement) {
        rootElement.dispatchEvent(new CustomEvent('navigateToTab', { detail: { tabIndex } }));
      }
      // Also navigate to the URL for proper routing
      navigate(path);
    } else {
      // Fallback to direct navigation for unknown paths
      navigate(path);
    }
  };

  // Tutorial steps with navigation paths
  const steps: TutorialStep[] = [
    {
      id: 'welcome',
      title: `Welcome to Flashgate, ${userName}!`,
      subtitle: 'Your Gateway to Secure Cloud Management',
      description: 'Flashgate is your all-in-one platform for managing cloud storage, AI integrations, decentralized infrastructure, and more. Let us show you around!',
      illustration: (
        <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'center', height: '100%' }}>
          <Box sx={{ 
            width: 180, 
            height: 180, 
            borderRadius: '50%', 
            background: 'linear-gradient(135deg, #617BFF 0%, #8B5CF6 100%)',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            boxShadow: '0 20px 60px rgba(97, 123, 255, 0.4)',
          }}>
            <img src={flashgateLogo} alt="Flashgate" style={{ width: 100, height: 100, objectFit: 'contain' }} />
          </Box>
        </Box>
      ),
      color: '#617BFF',
      bgGradient: 'linear-gradient(135deg, #F0F2FF 0%, #E8ECFF 100%)',
    },
    {
      id: 'cloud-storage',
      title: 'Cloud Storage',
      subtitle: 'Unified Multi-Cloud Management',
      description: 'Connect and manage your cloud storage from AWS S3, Google Cloud Storage, and Azure Blob in one place. Monitor usage, manage buckets, and track your data across all providers.',
      mainPath: '/cloud-storage/overview',
      features: [
        { icon: <StorageIcon sx={{ color: '#10B981' }} />, title: 'Buckets', description: 'Create and manage storage buckets', path: '/cloud-storage/buckets', trackingKey: 'cloud-buckets' },
        { icon: <CloudStorageIcon size={20} />, title: 'Overview', description: 'Unified view of all resources', path: '/cloud-storage/overview', trackingKey: 'cloud-overview' },
        { icon: <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="#F59E0B" strokeWidth="2"><path d="M3 3v18h18"/><path d="M18.7 8l-5.1 5.2-2.8-2.7L7 14.3"/></svg>, title: 'Statistics', description: 'Monitor usage and bandwidth', path: '/metrics', trackingKey: 'cloud-stats' },
      ],
      illustration: (
        <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 3 }}>
          <Box sx={{ display: 'flex', gap: 2 }}>
            <Box sx={{ width: 64, height: 64, borderRadius: '12px', bgcolor: 'white', display: 'flex', alignItems: 'center', justifyContent: 'center', boxShadow: '0 8px 24px rgba(0, 0, 0, 0.1)', border: '1px solid #E5E7EB' }}>
              <img src={awsLogo} alt="AWS" style={{ width: 40, height: 40, objectFit: 'contain' }} />
            </Box>
            <Box sx={{ width: 64, height: 64, borderRadius: '12px', bgcolor: 'white', display: 'flex', alignItems: 'center', justifyContent: 'center', boxShadow: '0 8px 24px rgba(0, 0, 0, 0.1)', border: '1px solid #E5E7EB' }}>
              <img src={googleCloudLogo} alt="Google Cloud" style={{ width: 40, height: 40, objectFit: 'contain' }} />
            </Box>
            <Box sx={{ width: 64, height: 64, borderRadius: '12px', bgcolor: 'white', display: 'flex', alignItems: 'center', justifyContent: 'center', boxShadow: '0 8px 24px rgba(0, 0, 0, 0.1)', border: '1px solid #E5E7EB' }}>
              <img src={azureLogo} alt="Azure" style={{ width: 40, height: 40, objectFit: 'contain' }} />
            </Box>
          </Box>
          <Box sx={{ width: 100, height: 100, borderRadius: '50%', bgcolor: '#10B981', display: 'flex', alignItems: 'center', justifyContent: 'center', boxShadow: '0 16px 48px rgba(16, 185, 129, 0.3)' }}>
            <StorageIcon sx={{ fontSize: 48, color: 'white' }} />
          </Box>
        </Box>
      ),
      color: '#10B981',
      bgGradient: 'linear-gradient(135deg, #ECFDF5 0%, #D1FAE5 100%)',
    },
    {
      id: 'ai',
      title: 'AI Integration',
      subtitle: 'Secure AI Gateway & Guardrails',
      description: 'Manage your AI/LLM integrations with built-in security policies and guardrails. Monitor usage, enforce policies, and protect sensitive data in AI interactions.',
      mainPath: '/ai/overview',
      features: [
        { icon: <AIIcon size={20} color="#8B5CF6" />, title: 'LLM Management', description: 'Configure AI language models', path: '/ai/llm', trackingKey: 'ai-llm' },
        { icon: <SecurityIcon sx={{ color: '#EF4444' }} />, title: 'AI Policies', description: 'Set up guardrails for data protection', path: '/ai/policies', trackingKey: 'ai-policies' },
        { icon: <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="#F59E0B" strokeWidth="2"><line x1="18" y1="20" x2="18" y2="10"/><line x1="12" y1="20" x2="12" y2="4"/><line x1="6" y1="20" x2="6" y2="14"/></svg>, title: 'Statistics', description: 'Track token usage and violations', path: '/ai/statistics', trackingKey: 'ai-stats' },
      ],
      illustration: (
        <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
          <Box sx={{ 
            width: 140, 
            height: 140, 
            borderRadius: '24px', 
            background: 'linear-gradient(135deg, #8B5CF6 0%, #A78BFA 100%)',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            boxShadow: '0 20px 60px rgba(139, 92, 246, 0.4)',
            position: 'relative',
          }}>
            <SmartToyIcon sx={{ fontSize: 64, color: 'white' }} />
            <Box sx={{ 
              position: 'absolute', 
              top: -10, 
              right: -10, 
              width: 36, 
              height: 36, 
              borderRadius: '50%', 
              bgcolor: '#10B981',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              border: '3px solid white',
            }}>
              <SecurityIcon sx={{ fontSize: 18, color: 'white' }} />
            </Box>
          </Box>
        </Box>
      ),
      color: '#8B5CF6',
      bgGradient: 'linear-gradient(135deg, #F5F3FF 0%, #EDE9FE 100%)',
    },
    {
      id: 'network',
      title: 'Network',
      subtitle: 'Bridge Nodes Infrastructure',
      description: 'Manage your network of bridge nodes that connect traditional cloud storage with decentralized infrastructure. Monitor public nodes and configure private nodes.',
      mainPath: '/network/public',
      features: [
        { icon: <NetworkIcon size={20} color="#617BFF" />, title: 'Public Nodes', description: 'View public bridge nodes', path: '/network/public', trackingKey: 'network-public' },
        { icon: <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="#8B5CF6" strokeWidth="2"><rect x="3" y="11" width="18" height="11" rx="2" ry="2"/><path d="M7 11V7a5 5 0 0 1 10 0v4"/></svg>, title: 'Private Nodes', description: 'Deploy your own bridge nodes', path: '/network/private', trackingKey: 'network-private' },
      ],
      illustration: (
        <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 3 }}>
          <Box sx={{ 
            width: 70, 
            height: 70, 
            borderRadius: '16px', 
            bgcolor: '#617BFF',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            boxShadow: '0 12px 36px rgba(97, 123, 255, 0.4)',
          }}>
            <NetworkIcon size={32} color="white" />
          </Box>
          <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1 }}>
            <Box sx={{ width: 60, height: 4, bgcolor: '#617BFF', borderRadius: 2 }} />
            <Box sx={{ width: 60, height: 4, bgcolor: '#8B5CF6', borderRadius: 2 }} />
            <Box sx={{ width: 60, height: 4, bgcolor: '#10B981', borderRadius: 2 }} />
          </Box>
          <Box sx={{ 
            width: 70, 
            height: 70, 
            borderRadius: '16px', 
            bgcolor: '#8B5CF6',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            boxShadow: '0 12px 36px rgba(139, 92, 246, 0.4)',
          }}>
            <svg width="32" height="32" viewBox="0 0 24 24" fill="none" stroke="white" strokeWidth="2">
              <rect x="3" y="11" width="18" height="11" rx="2" ry="2"/>
              <path d="M7 11V7a5 5 0 0 1 10 0v4"/>
            </svg>
          </Box>
        </Box>
      ),
      color: '#F97316',
      bgGradient: 'linear-gradient(135deg, #FFF7ED 0%, #FFEDD5 100%)',
    },
    {
      id: 'repositories',
      title: 'Repositories',
      subtitle: 'Centralized Resource Management',
      description: 'Repositories are the core of Flashgate. They group your buckets, AI configurations, and API keys together. Create repositories to organize your resources and generate API keys for programmatic access.',
      mainPath: '/repositories/inventory',
      features: [
        { icon: <RepositoriesIcon size={20} color="#617BFF" />, title: 'Inventory', description: 'View and manage repositories', path: '/repositories/inventory', trackingKey: 'repos-inventory' },
        { icon: <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="#F59E0B" strokeWidth="2"><path d="M21 2l-2 2m-7.61 7.61a5.5 5.5 0 1 1-7.778 7.778 5.5 5.5 0 0 1 7.777-7.777zm0 0L15.5 7.5m0 0l3 3L22 7l-3-3m-3.5 3.5L19 4"/></svg>, title: 'API Keys', description: 'Generate and manage API keys', path: '/repositories/api-keys', trackingKey: 'repos-apikeys' },
        { icon: <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="#10B981" strokeWidth="2"><path d="M3 3v18h18"/><path d="M18.7 8l-5.1 5.2-2.8-2.7L7 14.3"/></svg>, title: 'Overview', description: 'Monitor repository statistics', path: '/repositories/overview', trackingKey: 'repos-overview' },
      ],
      illustration: (
        <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
          <Box sx={{ 
            width: 140, 
            height: 140, 
            borderRadius: '24px', 
            background: 'linear-gradient(135deg, #617BFF 0%, #818CF8 100%)',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            boxShadow: '0 20px 60px rgba(97, 123, 255, 0.4)',
            position: 'relative',
          }}>
            <FolderIcon sx={{ fontSize: 64, color: 'white' }} />
            <Box sx={{ 
              position: 'absolute', 
              bottom: -8, 
              right: -8, 
              width: 40, 
              height: 40, 
              borderRadius: '10px', 
              bgcolor: '#F59E0B',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              border: '3px solid white',
              boxShadow: '0 4px 12px rgba(245, 158, 11, 0.4)',
            }}>
              <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="white" strokeWidth="2">
                <path d="M21 2l-2 2m-7.61 7.61a5.5 5.5 0 1 1-7.778 7.778 5.5 5.5 0 0 1 7.777-7.777zm0 0L15.5 7.5m0 0l3 3L22 7l-3-3m-3.5 3.5L19 4"/>
              </svg>
            </Box>
          </Box>
        </Box>
      ),
      color: '#617BFF',
      bgGradient: 'linear-gradient(135deg, #EEF2FF 0%, #E0E7FF 100%)',
    },
    {
      id: 'chat',
      title: 'Private Chat',
      subtitle: 'AI-Powered Data Assistant',
      description: 'Chat with your data using our private AI assistant. Ask questions about your repositories, get insights, and interact with your data in natural language - all while keeping your data secure.',
      mainPath: '/chat',
      features: [
        { icon: <ChatIcon size={20} color="#EC4899" />, title: 'Natural Language', description: 'Ask questions in plain English', path: '/chat', trackingKey: 'chat-main' },
        { icon: <SecurityIcon sx={{ color: '#10B981' }} />, title: 'Private & Secure', description: 'Your data stays protected', path: '/chat', trackingKey: 'chat-secure' },
        { icon: <SmartToyIcon sx={{ color: '#8B5CF6' }} />, title: 'Context Aware', description: 'AI understands your repos', path: '/chat', trackingKey: 'chat-context' },
      ],
      illustration: (
        <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
          <Box sx={{ 
            width: 140, 
            height: 140, 
            borderRadius: '28px', 
            background: 'linear-gradient(135deg, #EC4899 0%, #F472B6 100%)',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            boxShadow: '0 20px 60px rgba(236, 72, 153, 0.4)',
          }}>
            <ChatIcon size={64} color="white" />
          </Box>
        </Box>
      ),
      color: '#EC4899',
      bgGradient: 'linear-gradient(135deg, #FDF2F8 0%, #FCE7F3 100%)',
    },
    {
      id: 'organization',
      title: 'Organization Settings',
      subtitle: 'Team & Workspace Management',
      description: 'Manage your organization, invite team members, configure workspaces, and handle billing. Set up policies that apply across your entire organization.',
      mainPath: '/organization',
      features: [
        { icon: <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="#617BFF" strokeWidth="2"><path d="M17 21v-2a4 4 0 0 0-4-4H5a4 4 0 0 0-4 4v2"/><circle cx="9" cy="7" r="4"/><path d="M23 21v-2a4 4 0 0 0-3-3.87"/><path d="M16 3.13a4 4 0 0 1 0 7.75"/></svg>, title: 'Team', description: 'Invite and manage members', path: '/organization', trackingKey: 'org-team' },
        { icon: <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="#8B5CF6" strokeWidth="2"><rect x="3" y="3" width="18" height="18" rx="2" ry="2"/><line x1="9" y1="3" x2="9" y2="21"/></svg>, title: 'Workspaces', description: 'Create project environments', path: '/organization', trackingKey: 'org-workspaces' },
        { icon: <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="#10B981" strokeWidth="2"><rect x="1" y="4" width="22" height="16" rx="2" ry="2"/><line x1="1" y1="10" x2="23" y2="10"/></svg>, title: 'Billing', description: 'Manage subscriptions', path: '/organization', trackingKey: 'org-billing' },
      ],
      illustration: (
        <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
          <Box sx={{ 
            width: 140, 
            height: 140, 
            borderRadius: '24px', 
            background: 'linear-gradient(135deg, #6366F1 0%, #818CF8 100%)',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            boxShadow: '0 20px 60px rgba(99, 102, 241, 0.4)',
          }}>
            <OrganizationIcon size={64} color="white" />
          </Box>
        </Box>
      ),
      color: '#6366F1',
      bgGradient: 'linear-gradient(135deg, #EEF2FF 0%, #E0E7FF 100%)',
    },
    {
      id: 'complete',
      title: "You're All Set!",
      subtitle: 'Ready to Get Started',
      description: "You've completed the tour! You can always revisit this tutorial from the Help menu. Now let's start building something amazing.",
      illustration: (
        <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center', gap: 2 }}>
          <Box sx={{ 
            width: 160, 
            height: 160, 
            borderRadius: '50%', 
            background: 'linear-gradient(135deg, #10B981 0%, #34D399 100%)',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            boxShadow: '0 20px 60px rgba(16, 185, 129, 0.4)',
          }}>
            <CheckCircleIcon sx={{ fontSize: 80, color: 'white' }} />
          </Box>
          {visitedSections.length > 0 && (
            <Typography sx={{ fontSize: '13px', color: '#667085', mt: 2 }}>
              {visitedSections.length} sections explored
            </Typography>
          )}
        </Box>
      ),
      color: '#10B981',
      bgGradient: 'linear-gradient(135deg, #ECFDF5 0%, #D1FAE5 100%)',
    },
  ];

  const currentStepData = steps[currentStep];
  const progress = ((currentStep + 1) / steps.length) * 100;
  const isFirstStep = currentStep === 0;
  const isLastStep = currentStep === steps.length - 1;

  const handleNext = () => {
    if (isLastStep) {
      // Clear tutorial in progress state when completing
      try {
        localStorage.removeItem('tutorialInProgress');
        localStorage.removeItem('tutorialCurrentStep');
      } catch { void 0; }
      onComplete();
    } else {
      setIsAnimating(true);
      setTimeout(() => {
        setCurrentStep((prev) => prev + 1);
        setIsAnimating(false);
      }, 150);
    }
  };

  const handleBack = () => {
    if (!isFirstStep) {
      setIsAnimating(true);
      setTimeout(() => {
        setCurrentStep((prev) => prev - 1);
        setIsAnimating(false);
      }, 150);
    }
  };

  const handleSkip = () => {
    // Clear tutorial in progress state when skipping
    try {
      localStorage.removeItem('tutorialInProgress');
      localStorage.removeItem('tutorialCurrentStep');
    } catch { void 0; }
    onClose();
  };

  // Reset step when modal opens, or resume from saved step
  useEffect(() => {
    if (open) {
      try {
        const savedStep = localStorage.getItem('tutorialCurrentStep');
        const inProgress = localStorage.getItem('tutorialInProgress');
        if (inProgress === 'true' && savedStep) {
          setCurrentStep(parseInt(savedStep, 10));
          // Clear the in-progress state since we're back in the tutorial
          localStorage.removeItem('tutorialInProgress');
          localStorage.removeItem('tutorialCurrentStep');
        } else {
          setCurrentStep(0);
        }
      } catch {
        setCurrentStep(0);
      }
    }
  }, [open]);

  if (!open) return null;

  return (
    <Box
      sx={{
        position: 'fixed',
        top: 0,
        left: 0,
        right: 0,
        bottom: 0,
        zIndex: 9999,
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        bgcolor: 'rgba(0, 0, 0, 0.6)',
        backdropFilter: 'blur(8px)',
      }}
    >
      <Box
        sx={{
          width: '90%',
          maxWidth: 900,
          maxHeight: '90vh',
          bgcolor: 'white',
          borderRadius: '24px',
          boxShadow: '0 25px 80px rgba(0, 0, 0, 0.25)',
          overflow: 'hidden',
          display: 'flex',
          flexDirection: 'column',
        }}
      >
        {/* Progress Bar */}
        <LinearProgress
          variant="determinate"
          value={progress}
          sx={{
            height: 4,
            bgcolor: '#E5E7EB',
            '& .MuiLinearProgress-bar': {
              bgcolor: currentStepData.color,
              transition: 'transform 0.3s ease',
            },
          }}
        />

        {/* Header */}
        <Box
          sx={{
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'space-between',
            px: 3,
            py: 2,
            borderBottom: '1px solid #E5E7EB',
          }}
        >
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
            <Box
              sx={{
                width: 36,
                height: 36,
                borderRadius: '10px',
                bgcolor: currentStepData.color,
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
              }}
            >
              <Typography sx={{ color: 'white', fontWeight: 700, fontSize: '14px' }}>
                {currentStep + 1}
              </Typography>
            </Box>
            <Typography sx={{ fontSize: '14px', color: '#667085' }}>
              Step {currentStep + 1} of {steps.length}
            </Typography>
          </Box>
          <IconButton
            onClick={handleSkip}
            sx={{
              color: '#9CA3AF',
              '&:hover': { bgcolor: '#F3F4F6', color: '#374151' },
            }}
          >
            <CloseIcon />
          </IconButton>
        </Box>

        {/* Content */}
        <Box
          sx={{
            display: 'flex',
            flex: 1,
            overflow: 'hidden',
            opacity: isAnimating ? 0 : 1,
            transform: isAnimating ? 'translateX(20px)' : 'translateX(0)',
            transition: 'all 0.15s ease',
          }}
        >
          {/* Left Side - Illustration */}
          <Box
            sx={{
              width: '45%',
              background: currentStepData.bgGradient,
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              p: 4,
            }}
          >
            {currentStepData.illustration}
          </Box>

          {/* Right Side - Content */}
          <Box
            sx={{
              width: '55%',
              p: 4,
              display: 'flex',
              flexDirection: 'column',
              justifyContent: 'center',
              overflow: 'auto',
            }}
          >
            {currentStepData.subtitle && (
              <Typography
                sx={{
                  fontSize: '12px',
                  fontWeight: 600,
                  color: currentStepData.color,
                  textTransform: 'uppercase',
                  letterSpacing: '0.5px',
                  mb: 1,
                }}
              >
                {currentStepData.subtitle}
              </Typography>
            )}
            <Typography
              sx={{
                fontSize: '28px',
                fontWeight: 700,
                color: '#101828',
                mb: 2,
                lineHeight: 1.2,
              }}
            >
              {currentStepData.title}
            </Typography>
            <Typography
              sx={{
                fontSize: '15px',
                color: '#667085',
                lineHeight: 1.7,
                mb: 3,
              }}
            >
              {currentStepData.description}
            </Typography>

            {/* Features List - Clickable */}
            {currentStepData.features && (
              <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
                {currentStepData.features.map((feature, index) => {
                  const isVisited = feature.trackingKey ? visitedSections.includes(feature.trackingKey) : false;
                  return (
                    <Box
                      key={index}
                      onClick={() => feature.path && handleNavigateToSection(feature.path, feature.trackingKey)}
                      sx={{
                        display: 'flex',
                        alignItems: 'flex-start',
                        gap: 2,
                        p: 2,
                        bgcolor: isVisited ? '#F0FDF4' : '#F9FAFB',
                        borderRadius: '12px',
                        border: isVisited ? '1px solid #BBF7D0' : '1px solid #E5E7EB',
                        cursor: feature.path ? 'pointer' : 'default',
                        transition: 'all 0.2s ease',
                        '&:hover': feature.path ? {
                          bgcolor: isVisited ? '#DCFCE7' : '#F3F4F6',
                          borderColor: isVisited ? '#86EFAC' : currentStepData.color,
                          transform: 'translateX(4px)',
                        } : {},
                      }}
                    >
                      <Box
                        sx={{
                          width: 36,
                          height: 36,
                          borderRadius: '8px',
                          bgcolor: 'white',
                          display: 'flex',
                          alignItems: 'center',
                          justifyContent: 'center',
                          flexShrink: 0,
                          border: '1px solid #E5E7EB',
                        }}
                      >
                        {feature.icon}
                      </Box>
                      <Box sx={{ flex: 1 }}>
                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 0.25 }}>
                          <Typography
                            sx={{ fontSize: '14px', fontWeight: 600, color: '#101828' }}
                          >
                            {feature.title}
                          </Typography>
                          {isVisited && (
                            <CheckCircleIcon sx={{ fontSize: 14, color: '#10B981' }} />
                          )}
                        </Box>
                        <Typography sx={{ fontSize: '13px', color: '#667085', lineHeight: 1.5 }}>
                          {feature.description}
                        </Typography>
                      </Box>
                      {feature.path && (
                        <Box sx={{ display: 'flex', alignItems: 'center', alignSelf: 'center' }}>
                          <OpenInNewIcon sx={{ fontSize: 16, color: '#9CA3AF' }} />
                        </Box>
                      )}
                    </Box>
                  );
                })}
              </Box>
            )}
          </Box>
        </Box>

        {/* Footer */}
        <Box
          sx={{
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'space-between',
            px: 3,
            py: 2.5,
            borderTop: '1px solid #E5E7EB',
            bgcolor: '#FAFAFA',
          }}
        >
          <Button
            onClick={handleSkip}
            sx={{
              color: '#667085',
              textTransform: 'none',
              fontSize: '14px',
              fontWeight: 500,
              '&:hover': { bgcolor: '#F3F4F6' },
            }}
          >
            Skip Tutorial
          </Button>

          <Box sx={{ display: 'flex', gap: 2 }}>
            {!isFirstStep && (
              <Button
                onClick={handleBack}
                startIcon={<ArrowBackIcon />}
                sx={{
                  color: '#374151',
                  textTransform: 'none',
                  fontSize: '14px',
                  fontWeight: 600,
                  border: '1px solid #D0D5DD',
                  borderRadius: '10px',
                  px: 2.5,
                  '&:hover': { bgcolor: '#F9FAFB', borderColor: '#9CA3AF' },
                }}
              >
                Back
              </Button>
            )}
            <Button
              onClick={handleNext}
              endIcon={isLastStep ? <CheckCircleIcon /> : <ArrowForwardIcon />}
              sx={{
                bgcolor: currentStepData.color,
                color: 'white',
                textTransform: 'none',
                fontSize: '14px',
                fontWeight: 600,
                borderRadius: '10px',
                px: 3,
                '&:hover': {
                  bgcolor: currentStepData.color,
                  filter: 'brightness(0.9)',
                },
              }}
            >
              {isFirstStep ? 'Start Tour' : isLastStep ? 'Get Started' : 'Next'}
            </Button>
          </Box>
        </Box>
      </Box>
    </Box>
  );
};

export default OnboardingTutorial;

// Floating bubble component to return to tutorial
interface TutorialReturnBubbleProps {
  onReturnToTutorial: () => void;
}

export const TutorialReturnBubble: React.FC<TutorialReturnBubbleProps> = ({ onReturnToTutorial }) => {
  const [isVisible, setIsVisible] = useState(false);
  const [isHovered, setIsHovered] = useState(false);

  useEffect(() => {
    // Check if tutorial is in progress
    const checkTutorialState = () => {
      try {
        const inProgress = localStorage.getItem('tutorialInProgress');
        setIsVisible(inProgress === 'true');
      } catch {
        setIsVisible(false);
      }
    };

    checkTutorialState();
    // Check on storage changes (for cross-tab sync)
    window.addEventListener('storage', checkTutorialState);
    // Also check periodically in case localStorage changes in same tab
    const interval = setInterval(checkTutorialState, 500);

    return () => {
      window.removeEventListener('storage', checkTutorialState);
      clearInterval(interval);
    };
  }, []);

  const handleClick = () => {
    onReturnToTutorial();
  };

  const handleDismiss = (e: React.MouseEvent) => {
    e.stopPropagation();
    try {
      localStorage.removeItem('tutorialInProgress');
      localStorage.removeItem('tutorialCurrentStep');
    } catch { void 0; }
    setIsVisible(false);
  };

  if (!isVisible) return null;

  return (
    <Box
      onMouseEnter={() => setIsHovered(true)}
      onMouseLeave={() => setIsHovered(false)}
      onClick={handleClick}
      sx={{
        position: 'fixed',
        bottom: 24,
        right: 24,
        zIndex: 9998,
        display: 'flex',
        alignItems: 'center',
        gap: 1.5,
        bgcolor: '#617BFF',
        color: 'white',
        borderRadius: isHovered ? '16px' : '50px',
        boxShadow: '0 8px 32px rgba(97, 123, 255, 0.4)',
        cursor: 'pointer',
        transition: 'all 0.3s cubic-bezier(0.4, 0, 0.2, 1)',
        overflow: 'hidden',
        px: isHovered ? 2.5 : 1.5,
        py: 1.5,
        '&:hover': {
          transform: 'scale(1.05)',
          boxShadow: '0 12px 40px rgba(97, 123, 255, 0.5)',
        },
      }}
    >
      {/* Animated pulse ring */}
      <Box
        sx={{
          position: 'absolute',
          top: '50%',
          left: '50%',
          transform: 'translate(-50%, -50%)',
          width: '100%',
          height: '100%',
          borderRadius: 'inherit',
          border: '2px solid rgba(255, 255, 255, 0.3)',
          animation: 'pulse-ring 2s infinite',
          '@keyframes pulse-ring': {
            '0%': { transform: 'translate(-50%, -50%) scale(1)', opacity: 1 },
            '100%': { transform: 'translate(-50%, -50%) scale(1.3)', opacity: 0 },
          },
        }}
      />
      
      {/* Icon */}
      <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'center', position: 'relative', zIndex: 1 }}>
        <svg width="24" height="24" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
          <path d="M12 22C17.5228 22 22 17.5228 22 12C22 6.47715 17.5228 2 12 2C6.47715 2 2 6.47715 2 12C2 17.5228 6.47715 22 12 22Z" stroke="white" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
          <path d="M12 16V12" stroke="white" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
          <path d="M12 8H12.01" stroke="white" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
        </svg>
      </Box>
      
      {/* Text - only visible on hover */}
      <Box
        sx={{
          display: 'flex',
          alignItems: 'center',
          gap: 1,
          maxWidth: isHovered ? 200 : 0,
          opacity: isHovered ? 1 : 0,
          transition: 'all 0.3s ease',
          overflow: 'hidden',
          whiteSpace: 'nowrap',
        }}
      >
        <Typography sx={{ fontSize: '14px', fontWeight: 600 }}>
          Continue Tutorial
        </Typography>
        <ArrowForwardIcon sx={{ fontSize: 18 }} />
      </Box>

      {/* Close button - visible on hover */}
      {isHovered && (
        <IconButton
          size="small"
          onClick={handleDismiss}
          sx={{
            p: 0.5,
            color: 'rgba(255, 255, 255, 0.7)',
            '&:hover': { color: 'white', bgcolor: 'rgba(255, 255, 255, 0.1)' },
          }}
        >
          <CloseIcon sx={{ fontSize: 16 }} />
        </IconButton>
      )}
    </Box>
  );
};
